import Header from '../../../components/feature/Header';
import Footer from '../../../components/feature/Footer';
import Button from '../../../components/base/Button';
import { useSEO, generateServiceSchema, generateBreadcrumbSchema } from '../../../utils/seo';

export default function GurneyTransport() {
  const serviceSchema = generateServiceSchema(
    'Gurney Medical Transportation',
    'Professional gurney and stretcher medical transportation for bedridden patients. Specialized equipment and trained medical transport staff ensure safe, comfortable transport throughout Orange County.'
  );
  
  const breadcrumbSchema = generateBreadcrumbSchema([
    { name: 'Home', url: '/' },
    { name: 'Services', url: '/#services' },
    { name: 'Gurney Transport', url: '/services/gurney' }
  ]);

  const combinedSchema = {
    '@context': 'https://schema.org',
    '@graph': [serviceSchema, breadcrumbSchema]
  };

  useSEO({
    title: 'Gurney Medical Transportation Westminster CA | Stretcher Transport Services',
    description: 'Professional gurney and stretcher medical transportation in Westminster CA and Orange County. Specialized equipment for bedridden patients. Safe, comfortable transport with trained staff.',
    keywords: 'gurney transport, stretcher transportation, bedridden patient transport, medical stretcher service, gurney NEMT Westminster CA',
    canonical: `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/services/gurney`,
    schema: combinedSchema
  });

  return (
    <div className="min-h-screen">
      <Header />
      
      {/* Hero Section */}
      <section 
        className="relative py-20 bg-cover bg-center"
        style={{
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.6), rgba(0, 0, 0, 0.6)), url('https://public.readdy.ai/ai/img_res/603b6bd24899eaaa2c99c5e3964747de.jpg')`
        }}
      >
        <div className="container mx-auto px-4 text-center text-white">
          <div className="max-w-4xl mx-auto">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              Gurney & Stretcher Transportation
            </h1>
            <p className="text-xl md:text-2xl mb-8 text-gray-200">
              Specialized medical transport for patients who need to remain lying down during transport
            </p>
          </div>
        </div>
      </section>

      {/* Gurney Transport Overview */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                Professional Gurney Transportation Services
              </h2>
              <p className="text-lg text-gray-600 mb-6">
                InteCare provides reliable, medical-ready gurney transportation designed for patients who require safe, secure, and fully assisted non-emergency transport. Our vehicles are purpose-built for mobility and medical support: every unit is handicap accessible and outfitted with a wheelchair lift, ramps, and onboard oxygen tanks to accommodate individuals with limited mobility or respiratory needs.
Each trip is handled by a trained driver who is certified in First Aid and CPR. They’re equipped to manage transfers, monitor comfort throughout the ride, and ensure the patient is safely loaded, secured, and transported from origin to destination.

InteCare’s gurney service delivers a controlled, supportive experience for seniors, post-operative patients, and anyone requiring lying-down transport with professional oversight  without unnecessary complexity or delays.

              </p>
            </div>
            <div>
              <img 
                src="https://static.readdy.ai/image/d32fda172531ebc64455c87992c4a988/6c30f60ec2044bfa5e3ce01ffb970785.png"
                alt="Gurney Transport Interior"
                className="w-full rounded-lg shadow-lg object-cover object-top"
              />
            </div>
          </div>
        </div>
      </section>

      {/* When Gurney Transport is Needed */}
      <section className="py-16 bg-red-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              When is Gurney Transport Needed?
            </h2>
            <p className="text-xl text-gray-600">
              Specialized medical transport for critical care situations
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-white rounded-xl p-8 shadow-lg text-center">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-hospital-line text-2xl text-red-600"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Hospital Discharge</h3>
              <p className="text-gray-600">
                Safe transport home or to care facilities for patients who cannot sit upright after treatment.
              </p>
            </div>
            
            <div className="bg-white rounded-xl p-8 shadow-lg text-center">
              <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-calendar-check-line text-2xl text-orange-600"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Medical Appointments</h3>
              <p className="text-gray-600">
                Comfortable transport to and from medical appointments for patients who need to remain lying down.
              </p>
            </div>
            
            <div className="bg-white rounded-xl p-8 shadow-lg text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-surgical-mask-line text-2xl text-blue-600"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Post-Surgery Transport</h3>
              <p className="text-gray-600">
                Transport after major surgeries when patients must remain in a supine position during recovery.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Medical Equipment & Staff */}
      {/* Section removed */}

      {/* Safety Protocols */}
      {/* Section removed */}

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-red-600 to-red-800">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Need Gurney Care Transport?
          </h2>
          <p className="text-xl text-red-100 mb-8 max-w-3xl mx-auto">
            Trust InteCare for professional gurney and stretcher transportation with basic life support and continuous medical supervision.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              variant="outline" 
              size="lg"
              onClick={() => window.location.href = '/contact'}
              className="border-2 border-blue-500 bg-blue-500 text-white hover:bg-blue-600 hover:border-blue-600 text-lg px-8 py-4"
            >
              <i className="ri-phone-line mr-2"></i>
              Call (714) 798-7318
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}